import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { api } from "../../api/api";
import { DatePicker, Typography, Row, Col, Card } from "antd";
import { ProfitExpensesRes } from "../../types/profitExpenses.type";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";
import { useNavigate } from "react-router-dom";
import { alPechkaMachine } from "../../config/config";

const { Title } = Typography;
const { RangePicker } = DatePicker;

const COLORS = [
  "#4caf50",
  "#f44336",
  "#2196f3",
  "#ff9800",
  "#be4bdb",
  "#ffc32d",
];

export default function ProfitExpenses() {
  const navigate = useNavigate();
  const [data, setData] = useState<ProfitExpensesRes>();
  const [loading, setLoading] = useState<boolean>(true);
  const [dateRange, setDateRange] = useState<[any, any] | undefined>(undefined);
  const getDefaultDateRange = () => {
    const startOfMonth = dayjs().startOf("month");
    const endOfMonth = dayjs().endOf("month");
    return [startOfMonth, endOfMonth];
  };

  useEffect(() => {
    const defaultRange = getDefaultDateRange();
    // @ts-ignore
    setDateRange(defaultRange);
  }, []);

  const fetchData = async () => {
    setLoading(true);

    try {
      const response = await api.get("/stats/profit-credits-expenses", {
        params: {
          startDate: dateRange ? dateRange[0]?.format("DD.MM.YY") : undefined,
          endDate: dateRange ? dateRange[1]?.format("DD.MM.YY") : undefined,
        },
      });

      setData(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const netProfitUSD = data
    ? (data.dateFiltered.totalProfitUSD || 0) +
      (data.dateFiltered.totalMachineProfitUSD || 0) +
      (data.dateFiltered.totalCreditInUSD || 0) +
      (data.dateFiltered.totalAuditProfitInUSD || 0) -
      (data.dateFiltered.totalExpenseInUSD || 0) -
      (data.dateFiltered.totalAuditExpenseInUSD || 0) -
      (data.dateFiltered.totalCommissionUSD || 0)
    : 0;

  const allTimeNetProfitUSD = data
    ? (data.totalStats.totalProfitUSDAllTime || 0) +
      (data.totalStats.totalMachineProfitUSDAllTime || 0) +
      (data.totalStats.totalCreditInUSDAllTime || 0) +
      (data.totalStats.totalAuditProfitInUSDAllTime || 0) -
      (data.totalStats.totalExpenseInUSDAllTime || 0) -
      (data.totalStats.totalAuditExpenseInUSDAllTime || 0) -
      (data.totalStats.totalCommissionUSDAllTime || 0)
    : 0;

  const usdPieData = [
    { name: "Доход", value: roundToThreeDecimals(data?.dateFiltered.totalProfitUSD || 0) },
    {
      name: "Прибыль печка",
      value: roundToThreeDecimals(data?.dateFiltered.totalMachineProfitUSD || 0),
    },
    {
      name: "Прочие доход",
      value: roundToThreeDecimals(data?.dateFiltered.totalCreditInUSD || 0),
    },
    { name: "Чистая прибыль", value: roundToThreeDecimals(netProfitUSD || 0) },
    {
      name: "Расходы",
      value: roundToThreeDecimals(data?.dateFiltered.totalExpenseInUSD || 0),
    },
    {
      name: "Ревизия",
      value: roundToThreeDecimals(
        (data?.dateFiltered.totalAuditProfitInUSD || 0) - (data?.dateFiltered.totalAuditExpenseInUSD || 0)
      ),
    },
    {
      name: "Расход коммисия",
      value: roundToThreeDecimals(data?.dateFiltered.totalCommissionUSD || 0),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Прибыли и убытки</Title>

      <RangePicker
        style={{ marginBottom: 20 }}
        onChange={(dates) => setDateRange(dates ?? undefined)}
        value={dateRange}
      />

      {loading ? (
        <Typography.Text>Загрузка...</Typography.Text>
      ) : (
        <>
          <Row gutter={16}>
            <Col span={12}>
              <Card title="USD">
                <Typography.Text
                  onClick={() =>
                    dateRange &&
                    navigate(
                      `/${dateRange[0]?.format(
                        "DD.MM.YY"
                      )}/${dateRange[1]?.format("DD.MM.YY")}/profits`
                    )
                  }
                  style={{ cursor: "pointer" }}
                >
                  Доход: ${roundToThreeDecimals(data?.dateFiltered.totalProfitUSD || 0)}
                </Typography.Text>
                <br />
                <Typography.Text
                  onClick={() =>
                    dateRange &&
                    navigate(
                      `/${dateRange[0]?.format(
                        "DD.MM.YY"
                      )}/${dateRange[1]?.format("DD.MM.YY")}/logs`
                    )
                  }
                  style={{ cursor: "pointer" }}
                >
                  Прибыль печка: $
                  {roundToThreeDecimals(data?.dateFiltered.totalMachineProfitUSD || 0)}
                </Typography.Text>
                <br />
                <Typography.Text>
                  Прочие доходы: $
                  {roundToThreeDecimals(data?.dateFiltered.totalCreditInUSD || 0)}
                </Typography.Text>
                <br />
                <Typography.Text
                  onClick={() =>
                    dateRange &&
                    navigate(
                      `/${dateRange[0]?.format(
                        "DD.MM.YY"
                      )}/${dateRange[1]?.format("DD.MM.YY")}/expenses`
                    )
                  }
                  style={{ cursor: "pointer" }}
                >
                  Расходы: ${roundToThreeDecimals(data?.dateFiltered.totalExpenseInUSD || 0)}
                </Typography.Text>
                <br />
                <Typography.Text
                  onClick={() =>
                    dateRange &&
                    navigate(
                      `/${dateRange[0]?.format(
                        "DD.MM.YY"
                      )}/${dateRange[1]?.format("DD.MM.YY")}/audit-actions`
                    )
                  }
                  style={{ cursor: "pointer" }}
                >
                  Ревизия: $
                  {roundToThreeDecimals(
                    (data?.dateFiltered.totalAuditProfitInUSD || 0) -
                    (data?.dateFiltered.totalAuditExpenseInUSD || 0)
                  )}
                </Typography.Text>
                <br />
                <Typography.Text
                  onClick={() =>
                    dateRange &&
                    navigate(
                      `/${dateRange[0]?.format(
                        "DD.MM.YY"
                      )}/${dateRange[1]?.format("DD.MM.YY")}/commisions`
                    )
                  }
                  style={{ cursor: "pointer" }}
                >
                  Расход коммисия: ${" "}
                  {roundToThreeDecimals(data?.dateFiltered.totalCommissionUSD || 0)}
                </Typography.Text>
                <br />
                <Typography.Text>
                  Чистая прибыль: ${roundToThreeDecimals(netProfitUSD || 0)}
                </Typography.Text>

                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={usdPieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label
                    >
                      {usdPieData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Партнёрские инвестиции" style={{ background: '#f6ffed', borderColor: '#52c41a' }}>
                <Typography.Text style={{ fontWeight: 'bold', color: '#1890ff', fontSize: '16px' }}>
                  💰 Общие инвестиции: ${roundToThreeDecimals(data?.dateFiltered.totalInvestments || 0)}
                </Typography.Text>
                <br />
                <Typography.Text style={{ fontWeight: 'bold', color: '#722ed1', fontSize: '16px' }}>
                  🏦 Выведенные инвестиции: ${roundToThreeDecimals(data?.dateFiltered.totalWithdrawnInvestment || 0)}
                </Typography.Text>
                <br />
                <Typography.Text style={{ fontWeight: 'bold', color: '#13c2c2', fontSize: '16px' }}>
                  💼 Баланс инвестиций: ${roundToThreeDecimals(data?.dateFiltered.totalBalanceInvestment || 0)}
                </Typography.Text>
                <br />
                <Typography.Text style={{ fontWeight: 'bold', color: '#52c41a', fontSize: '16px' }}>
                  🌍 Чистая прибыль за все время: ${roundToThreeDecimals(allTimeNetProfitUSD || 0)}
                </Typography.Text>
                <br />
                <Typography.Text style={{ fontWeight: 'bold', color: '#fa8c16', fontSize: '16px' }}>
                  📈 Выведенная прибыль: ${roundToThreeDecimals(data?.dateFiltered.totalWithdrawnProfit || 0)}
                </Typography.Text>
                <br />
                <Typography.Text style={{ fontWeight: 'bold', color: '#52c41a', fontSize: '16px' }}>
                  📊 Баланс прибыль партнёров: ${roundToThreeDecimals(data?.dateFiltered.totalPartnersBalance || 0)}
                </Typography.Text>
                <br />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}
